import { createContext, useContext, useState, useEffect } from 'react';

export type RightDrawerType = {
  drawerHeight: number;
};
export type RightDrawerDispatch = (
  arg1: ((arg1: RightDrawerType) => RightDrawerType) | RightDrawerType
) => void;

const RightDrawerContext = createContext<Nullable<RightDrawerType>>(undefined);
const RightDrawerDispatchContext = createContext<Nullable<RightDrawerDispatch>>(undefined);

export type RightDrawerProps = {
  children: React.ReactNode;
  onChange?: (rightDrawer: RightDrawerType) => void;
};

/**
 * Resizable drawer on the right
 */
export const RightDrawerProvider = ({
  children,
  onChange,
}: RightDrawerProps): React.ReactElement => {
  const [rightDrawer, setRightDrawer] = useState<RightDrawerType>({ drawerHeight: 0 });

  useEffect(() => {
    onChange?.(rightDrawer);
  }, [rightDrawer, onChange]);

  return (
    <RightDrawerContext.Provider value={rightDrawer}>
      <RightDrawerDispatchContext.Provider value={setRightDrawer}>
        {children}
      </RightDrawerDispatchContext.Provider>
    </RightDrawerContext.Provider>
  );
};

export const useRightDrawer = (): RightDrawerType => {
  const RightDrawer = useContext(RightDrawerContext);

  if (!RightDrawer) {
    throw new Error('useRightDrawer must be used within a RightDrawerProvider.');
  }

  return RightDrawer;
};

export const useRightDrawerDispatch = (): RightDrawerDispatch => {
  const RightDrawerDispatch = useContext(RightDrawerDispatchContext);

  if (!RightDrawerDispatch) {
    throw new Error('useRightDrawerDispatch must be used within a RightDrawerProvider.');
  }

  return RightDrawerDispatch;
};

export const useOptionalRightDrawerDispatch = (): Nullable<RightDrawerDispatch> => {
  return useContext(RightDrawerDispatchContext);
};

export const useRightDrawerResizable = (): [RightDrawerType, RightDrawerDispatch] => {
  return [useRightDrawer(), useRightDrawerDispatch()];
};
